import styles from "./task-card.module.scss";
import primaryArrow from "assets/images/v2/Primary_Arrow.svg";
import secondaryArrow from "assets/images/v2/Secondary_Arrow.svg";
import { getCurrentUserId, getCurrentUserName, getDealId, getEncompassLoanId, getLoanNumber, getNhDealApplicationId, getProspectId, setProfileActiveTab, setSelectedTaskId } from "features/authSlice";
import { ESignFSEventType, NeedStatus, NeedItem, NeedType, useGetNeedsQuery, useMarkESignFSEventSentMutation } from "pages/dashboard/home/dashboardService";
import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Spinner } from "../spinner/spinner";
import { AdditionalDocumentationCondition, DISCLOSURE_TITLE_PREFIX, CLOSING_DISCLOSURE_TITLE_PREFIX } from "utilities/Constant";
import useDesktopChecker from "hooks/useDesktopChecker";
import { isNonEmptyString } from "utilities/string";
import Pill from "../pill/pill";
import { trackFSESignCompleted, trackFSESignCreated } from "utilities/full-story";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";

type TaskCardProps = {
  readonly onNeedsLoaded?: (hasNeeds: boolean) => void;
};

export default function TaskCard({ onNeedsLoaded }: TaskCardProps) {
  const [needsList, setNeedsList] = useState<NeedItem[]>([]);
  const [allTasksHaveErrors, setAllTasksHaveErrors] = useState(false);
  const userId = useSelector(getCurrentUserId);
  const username = useSelector(getCurrentUserName);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const prospectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const losGuid = useSelector(getEncompassLoanId);
  const loanProgress = useSelector(getLoanProgress);
  const dispatch = useDispatch();
  const isDesktop = useDesktopChecker();
  const eventKeysInFlightRef = useRef(new Set<string>());
  const [markESignFSEventSent] = useMarkESignFSEventSentMutation();

  const shouldSkipQuery = !nhDealApplicationId || !userId;

  const {
    data: needItems,
    isLoading: isNeedsListLoading,
    isFetching,
    isUninitialized,
    error,
  } = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: shouldSkipQuery,
      refetchOnMountOrArgChange: true,
    },
  );

  const isDataLoading = shouldSkipQuery || isUninitialized || isNeedsListLoading || isFetching;
  useEffect(() => {
    const nonCompletedTasks = (needItems ?? []).filter(
      (item) =>
        item.needStatus?.toLowerCase() != NeedStatus.Satisfied.toLowerCase() &&
        item.needStatus?.toLowerCase() != NeedStatus.ReviewNeeded.toLowerCase() &&
        item.needStatus?.toLowerCase() != NeedStatus.Completed.toLowerCase() &&
        item.title !== AdditionalDocumentationCondition,
    );

    const anyHaveErrors = nonCompletedTasks.some(item => !!item.taskLoadErrorType);
    setAllTasksHaveErrors(anyHaveErrors);

    // If ANY task has an error, don't show ANY tasks - just show error message
    if (anyHaveErrors) {
      setNeedsList([]);
      onNeedsLoaded?.(false);
      return;
    }

    // Only show tasks if ALL services succeeded
    const filteredList = nonCompletedTasks.filter(item => !item.taskLoadErrorType);
    setNeedsList(filteredList);

    onNeedsLoaded?.(filteredList.length > 0);

    if (filteredList.length > 0) {
      setSelectedTaskId(filteredList[0].id);
    }
  }, [needItems]);

  useEffect(() => {
    if (!needItems?.length || !nhDealApplicationId || !userId) {
      return;
    }

    const trackEvent = async (item: NeedItem, eventType: ESignFSEventType) => {
      if (!item.title || !item.packageId) {
        return;
      }

      const eventKey = `${item.id}:${eventType}`;
      if (eventKeysInFlightRef.current.has(eventKey)) {
        return;
      }

      eventKeysInFlightRef.current.add(eventKey);

      const baseProps = {
        username,
        prospectId: item.prospectId ?? prospectId,
        dealId: dealId || null,
        timestamp: new Date().toISOString(),
        losLoanNumber: loanNumber || null,
        losGuid: losGuid || null,
        loanMilestone: loanProgress?.status ?? "Unknown",
      };

      try {
        if (eventType === ESignFSEventType.Created) {
          trackFSESignCreated(baseProps);
        } else {
          trackFSESignCompleted(baseProps);
        }

        // Determine which title prefix const to send based on the normalized title
        const normalizedTitle = item.title?.replace(/\s/g, "").toLowerCase();
        let titlePrefixValue: string | undefined;
        
        if (normalizedTitle?.startsWith(DISCLOSURE_TITLE_PREFIX)) {
          titlePrefixValue = DISCLOSURE_TITLE_PREFIX;
        } else if (normalizedTitle?.startsWith(CLOSING_DISCLOSURE_TITLE_PREFIX)) {
          titlePrefixValue = CLOSING_DISCLOSURE_TITLE_PREFIX;
        }

        // Only trigger API for the two allowed title types
        if (titlePrefixValue) {
          await markESignFSEventSent({
            nhDealApplicationId,
            prospectId: item.prospectId ?? prospectId,
            packageId: item.packageId,
            titlePrefix: titlePrefixValue,
            eventType,
          }).unwrap();
        }
      } catch {
        eventKeysInFlightRef.current.delete(eventKey);
      }
    };

    needItems
      .filter((item) => {
        if (item.needType !== NeedType.ESign) return false;
        const normalizedTitle = item.title?.replace(/\s/g, "").toLowerCase();
        return normalizedTitle?.startsWith(DISCLOSURE_TITLE_PREFIX) || 
               normalizedTitle?.startsWith(CLOSING_DISCLOSURE_TITLE_PREFIX);
      })
      .forEach((item) => {
        if (
          item.isEnabled &&
          item.needStatus?.toLowerCase() === NeedStatus.New.toLowerCase() &&
          !item.isEsignCreatedFSEventSent
        ) {
          void trackEvent(item, ESignFSEventType.Created);
        }

        if (
          item.isEnabled &&
          item.needStatus?.toLowerCase() === NeedStatus.Completed.toLowerCase() &&
          !item.isEsignCompletedFSEventSent
        ) {
          void trackEvent(item, ESignFSEventType.Completed);
        }
      });
  }, [needItems]);

  const handleViewAllClick = () => {
    dispatch(setProfileActiveTab(NavigationIds.Tasks));
  };

  const handleTaskClick = (task: NeedItem) => {
    dispatch(setSelectedTaskId(task.id));
    dispatch(setProfileActiveTab(NavigationIds.Tasks));
  };

  return (
    <div className={styles.taskCard}>
      <div className={styles.heading}>
        {isDesktop && <h5 className="heading-medium">My Tasks</h5>}
        {!isDesktop && <h6 className="heading-medium">My Tasks</h6>}
      </div>
      <div className={styles.content2}>
        {error && <div className={styles.errorMessage}>We are having issues loading your tasks. Try again later.</div>}
        {isDataLoading && (
          <div className={styles.spinnerContainer}>
            <Spinner isApi />
          </div>
        )}
        
        {allTasksHaveErrors && !isDataLoading && !error && (
          <div className={styles.errorMessage}>
            We are having issues loading your tasks. Try again later.
          </div>
        )}
        
        <div className={styles.taskContent1}>
          {needsList
            .filter((item) => item.needStatus?.toLowerCase() != NeedStatus.Completed.toLowerCase())
            .slice(0, 3)
            .map((task) => (
              <button
                className={styles.taskContentParent}
                key={task.title}
                onClick={() => handleTaskClick(task)}
                aria-label={`Select task: ${task.title}`}
              >
                <div className={styles.frameChild} />
                <div className={styles.taskContent2}>
                  <div className={styles.rectangleParent}>
                    <div className={styles.taskDetails}>
                      <div className={styles.taskTile}>{task.title}</div>
                      {isNonEmptyString(task.assignedTo) && <Pill text={task.assignedTo || ""} variant="assigned" />}
                      {(task.isNewComment === true|| !!task.note?.trim()) && <Pill text="New Comments" variant="comments" />}
                    </div>
                  </div>
                </div>
                <img className={styles.primaryIcon} alt="Chevron right icon" src={secondaryArrow} />
              </button>
            ))}

          {needsList.length === 0 && !isDataLoading && !error && !allTasksHaveErrors && (
            <div className={styles.noTasksMessageContainer}>
              <div className={styles.noTasksMessage}>You're all caught up!</div>
            </div>
          )}

          {needsList.length >= 3 && (
            <div className={styles.taskContentInner}>
              <button className={styles.viewAllParent} onClick={handleViewAllClick} aria-label="View all tasks">
                <div className={styles.viewDetails}>View all</div>
                <img className={styles.chevronRightIcon} alt="Chevron right icon" src={primaryArrow} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
